import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { alsLokalesDatum, api, centAusText, errorText, formatDateTime, formatFrist, formatGeld } from '../api';
import { useAuth } from '../auth';
import type { Aufgabe, BenutzerKurz, Notiz, Position, Vorgang, VorgangArt } from '../types';
import { Badge, Card, Dialog, Field, Notice, TYP_SYMBOL, kundenName } from './ui';

export const PFAD: Record<VorgangArt, string> = { bestellung: '/bestellungen', service: '/service', lieferung: '/lieferungen', forderung: '/forderungen' };
const API: Record<VorgangArt, string> = { bestellung: '/api/bestellungen', service: '/api/service', lieferung: '/api/lieferungen', forderung: '/api/forderungen' };
export const SYMBOL: Record<VorgangArt, string> = { bestellung: '🛒', service: '🔧', lieferung: '🚚', forderung: '💶' };
const LISTE: Record<VorgangArt, string> = { bestellung: 'Bestellungen', service: 'Service', lieferung: 'Lieferungen', forderung: 'Forderungen' };

/** Status in Kontakt mit dem Kunden - dort gibt es "nicht erreicht". Spiegelt den Server. */
const KONTAKT_STATUS: Record<VorgangArt, string[]> = {
  bestellung: ['eingetroffen', 'kunde_informiert'],
  service: ['kva_beim_kunden', 'abholbereit', 'kunde_informiert', 'termin_offen'],
  lieferung: ['erfasst'],
  forderung: ['zahlung_offen'],
};

// ---------------------------------------------------------------------------
// Karten
// ---------------------------------------------------------------------------

function FristSpalte({ status, faellig, ueberfaellig, eskaliert }: { status: string; faellig: string | null; ueberfaellig: boolean; eskaliert: string | null }) {
  if (status === 'abgeschlossen') return <span className="badge ok">abgeschlossen</span>;
  if (status === 'storniert') return <span className="badge">storniert</span>;
  if (!faellig) return null;
  return (
    <>
      {ueberfaellig ? 'überfällig ' : 'fällig '}
      {formatFrist(faellig)}
      {eskaliert && <div className="badge bad">eskaliert</div>}
    </>
  );
}

const istHeute = (faellig: string | null, ueberfaellig: boolean) => !!faellig && !ueberfaellig && formatFrist(faellig).startsWith('heute');

/** Eine Zeile auf "Mein Tag" - egal ob Ticket, Bestellung oder Serviceauftrag. */
export function AufgabeKarte({ a, zeigeZustaendig }: { a: Aufgabe; zeigeZustaendig?: boolean }) {
  const pfad = a.art === 'ticket' ? `/tickets/${a.id}` : `${PFAD[a.art]}/${a.id}`;
  const symbol = a.art === 'ticket' ? TYP_SYMBOL[a.typ ?? 'sonstiges'] : SYMBOL[a.art];
  const kunde = kundenName({ vorname: a.kunde_vorname, nachname: a.kunde_nachname, firma: a.kunde_firma }) || a.telefon || 'unbekannter Anrufer';
  const klasse = ['ticket', a.ueberfaellig ? 'ueberfaellig' : '', istHeute(a.faellig_am, a.ueberfaellig) ? 'heute' : ''].join(' ');
  return (
    <Link to={pfad} className={klasse}>
      <span className="typ">{symbol}</span>
      <div>
        <div className="titel">{a.art === 'ticket' ? a.titel : a.aufgabe ?? a.status_bezeichnung}</div>
        <div className="meta">
          <span className="mono">{a.nummer}</span>
          <span>{kunde}</span>
          {a.art !== 'ticket' && <span>{a.titel}</span>}
          {a.warten_auf && <span>wartet auf: {a.warten_auf}</span>}
          {zeigeZustaendig && <span>→ {a.zustaendig}</span>}
          {a.kontaktversuche > 0 && <span>{a.kontaktversuche}× nicht erreicht</span>}
        </div>
      </div>
      <div className="frist">
        <FristSpalte status={a.status} faellig={a.faellig_am} ueberfaellig={a.ueberfaellig} eskaliert={a.eskaliert_am} />
      </div>
    </Link>
  );
}

/** Eine Zeile in den Listen Bestellungen / Werkstatt. */
export function VorgangKarte({ art, v, titel, zeigeZustaendig }: { art: VorgangArt; v: Vorgang; titel: string; zeigeZustaendig?: boolean }) {
  const offen = v.status !== 'abgeschlossen' && v.status !== 'storniert';
  const klasse = ['ticket', offen && v.ueberfaellig ? 'ueberfaellig' : '', offen && istHeute(v.faellig_am, v.ueberfaellig) ? 'heute' : ''].join(' ');
  return (
    <Link to={`${PFAD[art]}/${v.id}`} className={klasse}>
      <span className="typ">{SYMBOL[art]}</span>
      <div>
        <div className="titel">{titel}</div>
        <div className="meta">
          <span className="mono">{v.nummer}</span>
          <span>{kundenName({ vorname: v.kunde_vorname, nachname: v.kunde_nachname, firma: v.kunde_firma })}</span>
          {offen && (
            <span>
              <Badge kind="info">{v.status_bezeichnung}</Badge> {v.aufgabe}
            </span>
          )}
          {v.warten_auf && <span>wartet auf: {v.warten_auf}</span>}
          {v.standort && <span>{v.standort}</span>}
          {zeigeZustaendig && <span>→ {v.zustaendig}</span>}
        </div>
      </div>
      <div className="frist">
        <FristSpalte status={v.status} faellig={v.faellig_am} ueberfaellig={v.ueberfaellig} eskaliert={v.eskaliert_am} />
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Detail: Kopf, Verlauf, Kunde
// ---------------------------------------------------------------------------

export function VorgangKopf({ art, v, titel, rechts }: { art: VorgangArt; v: Vorgang; titel: ReactNode; rechts?: ReactNode }) {
  const offen = v.status !== 'abgeschlossen' && v.status !== 'storniert';
  return (
    <div className="kopf">
      <div>
        <div className="muted small">
          <Link to={PFAD[art]}>{LISTE[art]}</Link> / <span className="mono">{v.nummer}</span>
        </div>
        <h1>
          {SYMBOL[art]} {titel}
        </h1>
      </div>
      <span className="spacer" />
      {rechts}
      {offen ? (
        <>
          <Badge kind="info">{v.status_bezeichnung}</Badge>
          {v.faellig_am && <Badge kind={v.ueberfaellig ? 'bad' : 'default'}>{v.ueberfaellig ? 'überfällig' : 'fällig'} {formatFrist(v.faellig_am)}</Badge>}
          {v.eskaliert_am && <Badge kind="bad">eskaliert</Badge>}
        </>
      ) : (
        <Badge kind={v.status === 'abgeschlossen' ? 'ok' : 'default'}>{v.status === 'abgeschlossen' ? 'abgeschlossen' : 'storniert'}</Badge>
      )}
    </div>
  );
}

const NOTIZ_ART: Record<string, string> = {
  status: 'Status',
  verschoben: 'Wiedervorlage',
  nicht_erreicht: 'Nicht erreicht',
  weitergegeben: 'Weitergegeben',
  wiedereroeffnet: 'Wieder geöffnet',
  zahlung: 'Zahlung',
  einsatz: 'Einsatz',
};

export function VorgangVerlauf({ art, v, onNeu }: { art: VorgangArt; v: Vorgang; onNeu: (v: Vorgang) => void }) {
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState('');
  async function speichern() {
    try {
      onNeu(await api<Vorgang>(`${API[art]}/${v.id}/notiz`, { method: 'POST', body: { text } }));
      setText('');
    } catch (e) {
      setFehler(errorText(e));
    }
  }
  return (
    <Card title="Verlauf">
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <div className="row" style={{ flexWrap: 'nowrap', marginBottom: 12 }}>
        <input placeholder="Notiz hinzufügen …" value={text} onChange={(e) => setText(e.target.value)} />
        <button disabled={!text.trim()} onClick={() => void speichern()}>
          Speichern
        </button>
      </div>
      <div className="verlauf">
        {(v.notizen ?? []).map((n: Notiz) => (
          <div className="eintrag" key={n.id}>
            <div className="wann">
              {formatDateTime(n.angelegt_am)}
              <br />
              {n.benutzer}
            </div>
            <div>
              {n.art !== 'notiz' && <Badge>{NOTIZ_ART[n.art] ?? n.art}</Badge>} {n.text}
            </div>
          </div>
        ))}
        <div className="eintrag">
          <div className="wann">
            {formatDateTime(v.angelegt_am)}
            <br />
            {v.angelegt_von_name}
          </div>
          <div>Angelegt</div>
        </div>
      </div>
    </Card>
  );
}

export function KundeKasten({ v, children }: { v: Vorgang; children?: ReactNode }) {
  return (
    <div>
      <div className="small muted">Kunde</div>
      <Link to={`/kunden/${v.kunde_id}`}>
        {kundenName({ vorname: v.kunde_vorname, nachname: v.kunde_nachname, firma: v.kunde_firma })} <span className="mono muted">{v.kundennummer}</span>
      </Link>
      {v.kunde_telefone && v.kunde_telefone.length > 0 && (
        <div className="small">
          {v.kunde_telefone.map((t) => (
            <a key={t} href={`tel:${t.replace(/[^\d+]/g, '')}`} style={{ marginRight: 10 }}>
              ☎ {t}
            </a>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aktionen
// ---------------------------------------------------------------------------

export type Feld = 'lieferant' | 'bestellnr' | 'erwartetAm' | 'diagnose' | 'kva';

/** Ein nächster Schritt im Ablauf, wie er als Knopf erscheint. */
export interface Schritt {
  label: string;
  ziel?: string;
  abschluss?: boolean;
  primary?: boolean;
  felder?: Feld[];
  notizPflicht?: boolean;
  notizLabel?: string;
  kvaEntscheidung?: 'angenommen' | 'abgelehnt';
  hinweis?: string;
  /** Eigene Aktion statt Statuswechsel (z. B. Teile bestellen). */
  aktion?: () => void;
}

type Dialogart = { art: 'schritt'; schritt: Schritt } | { art: 'wiedervorlage' } | { art: 'weitergeben' } | { art: 'stornieren' } | { art: 'oeffnen' } | null;

type AbrechnungArt = 'kasse' | 'forderung' | 'ueber_service' | 'keine';

const ABRECHNUNG: { wert: AbrechnungArt; label: string; hinweis: string }[] = [
  { wert: 'kasse', label: 'An der Kasse bezahlt', hinweis: 'MehrData-Belegnummer' },
  { wert: 'forderung', label: 'Nicht bezahlt – Rechnung folgt / offen (legt eine Forderung an)', hinweis: 'MehrData-Rechnungsnummer, falls schon geschrieben' },
  { wert: 'ueber_service', label: 'Teile für Werkstatt – Abrechnung über den Serviceauftrag', hinweis: '' },
  { wert: 'keine', label: 'Ohne Berechnung (Garantie, Kulanz, im Preis enthalten …)', hinweis: 'Begründung' },
];

/**
 * Knöpfe für die nächsten Schritte plus die gemeinsamen Aktionen
 * (Wiedervorlage, Weitergeben, Nicht erreicht, Storno). Jede Aktion, die
 * etwas schliesst oder verschiebt, fragt nach Begründung bzw. Abrechnung.
 */
export function VorgangAktionen({
  art,
  v,
  schritte,
  onNeu,
  abschlussArten = ['kasse', 'forderung', 'keine'],
  offeneStatus,
}: {
  art: VorgangArt;
  v: Vorgang;
  schritte: Schritt[];
  onNeu: (v: Vorgang) => void;
  abschlussArten?: AbrechnungArt[];
  offeneStatus: { wert: string; label: string }[];
}) {
  const { user, can } = useAuth();
  const [dialog, setDialog] = useState<Dialogart>(null);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);
  const [benutzer, setBenutzer] = useState<BenutzerKurz[]>([]);
  // Formularfelder aller Dialoge
  const [notiz, setNotiz] = useState('');
  const [datum, setDatum] = useState('');
  const [eigeneFrist, setEigeneFrist] = useState(false);
  const [wartenAuf, setWartenAuf] = useState('');
  const [lieferant, setLieferant] = useState('');
  const [bestellnr, setBestellnr] = useState('');
  const [erwartetAm, setErwartetAm] = useState('');
  const [diagnose, setDiagnose] = useState('');
  const [kva, setKva] = useState('');
  const [an, setAn] = useState('');
  const [abrechnung, setAbrechnung] = useState<(typeof abschlussArten)[number]>(abschlussArten[0]!);
  const [beleg, setBeleg] = useState('');
  const [betrag, setBetrag] = useState('');
  const [zielStatus, setZielStatus] = useState('');

  useEffect(() => {
    void api<BenutzerKurz[]>('/api/benutzer/auswahl').then(setBenutzer);
  }, []);

  const offen = v.status !== 'abgeschlossen' && v.status !== 'storniert';
  const w = v as Vorgang & { lieferant?: string | null; diagnose?: string | null };

  function oeffne(d: Dialogart) {
    setFehler('');
    setNotiz('');
    setEigeneFrist(false);
    setDatum(alsLokalesDatum(new Date(Date.now() + 24 * 3600_000)));
    setWartenAuf(v.warten_auf ?? '');
    setLieferant(w.lieferant ?? '');
    setBestellnr('');
    setErwartetAm('');
    setDiagnose(w.diagnose ?? '');
    setKva('');
    setAn('');
    setBeleg('');
    setBetrag('');
    setAbrechnung(abschlussArten[0]!);
    setZielStatus(offeneStatus[0]?.wert ?? '');
    setDialog(d);
  }

  async function los(pfad: string, body: unknown) {
    setBusy(true);
    setFehler('');
    try {
      onNeu(await api<Vorgang>(`${API[art]}/${v.id}/${pfad}`, { method: 'POST', body }));
      setDialog(null);
    } catch (e) {
      setFehler(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  function bestaetigen() {
    if (!dialog) return;
    if (dialog.art === 'wiedervorlage') return void los('wiedervorlage', { faelligAm: new Date(datum).toISOString(), begruendung: notiz, wartenAuf: wartenAuf || null });
    if (dialog.art === 'weitergeben') return void los('weitergeben', { zustaendigId: an, notiz: notiz || undefined });
    if (dialog.art === 'stornieren') return void los('stornieren', { grund: notiz });
    if (dialog.art === 'oeffnen') return void los('wiedereroeffnen', { status: zielStatus, begruendung: notiz });
    const s = dialog.schritt;
    if (s.abschluss)
      return void los('abschliessen', {
        art: abrechnung,
        beleg: beleg || null,
        notiz: notiz || null,
        betragCent: abrechnung === 'forderung' ? centAusText(betrag) : undefined,
      });
    const kvaCent = s.felder?.includes('kva') ? centAusText(kva) : undefined;
    void los('status', {
      status: s.ziel,
      notiz: notiz || undefined,
      faelligAm: eigeneFrist ? new Date(datum).toISOString() : undefined,
      wartenAuf: wartenAuf || null,
      ...(s.felder?.includes('lieferant') ? { lieferant, lieferantBestellnr: bestellnr || null, erwartetAm: erwartetAm || null } : {}),
      ...(s.felder?.includes('diagnose') ? { diagnose } : {}),
      ...(kvaCent !== undefined ? { kvaCent } : {}),
      ...(s.kvaEntscheidung ? { kvaEntscheidung: s.kvaEntscheidung } : {}),
    });
  }

  const s = dialog?.art === 'schritt' ? dialog.schritt : null;
  const abrechnungBraucht = ABRECHNUNG.find((a) => a.wert === abrechnung)?.hinweis ?? '';
  const kannBestaetigen = (() => {
    if (!dialog) return false;
    if (dialog.art === 'weitergeben') return !!an;
    if (dialog.art === 'wiedervorlage' || dialog.art === 'stornieren' || dialog.art === 'oeffnen') return notiz.trim().length >= 3;
    if (s?.abschluss) {
      if (abrechnung === 'keine') return notiz.trim().length >= 3;
      if (abrechnung === 'forderung') return (centAusText(betrag) ?? 0) > 0;
      return abrechnung === 'ueber_service' || !!beleg.trim();
    }
    if (s?.notizPflicht && notiz.trim().length < 3) return false;
    if (s?.felder?.includes('lieferant') && !lieferant.trim()) return false;
    if (s?.felder?.includes('kva') && centAusText(kva) === null) return false;
    return true;
  })();

  if (!offen) {
    return (
      <div className="stack" style={{ gap: 8 }}>
        <div className="notice success" style={{ marginBottom: 0 }}>
          {v.status === 'storniert' ? (
            <>
              <strong>Storniert</strong> am {formatDateTime(v.abgeschlossen_am)}: {v.storno_grund}
            </>
          ) : (
            <>
              <strong>Abgeschlossen</strong> am {formatDateTime(v.abgeschlossen_am)}
              {v.abrechnung_art && <> – {ABRECHNUNG.find((a) => a.wert === v.abrechnung_art)?.label}</>}
              {v.forderung_id && (
                <>
                  {' '}
                  · <Link to={`/forderungen/${v.forderung_id}`}>zur Forderung</Link>
                </>
              )}
              {v.abrechnung_beleg && <> · Beleg <span className="mono">{v.abrechnung_beleg}</span></>}
              {v.abrechnung_notiz && <> · {v.abrechnung_notiz}</>}
            </>
          )}
        </div>
        {/* Eine Forderung öffnet sich nur durch Storno einer Zahlung wieder - sonst stimmt sie nicht mehr mit dem Geld überein. */}
        {can('admin') && art !== 'forderung' && (
          <div>
            <button className="klein" onClick={() => oeffne({ art: 'oeffnen' })}>
              Wieder öffnen
            </button>
          </div>
        )}
        {dialog && renderDialog()}
      </div>
    );
  }

  function renderDialog() {
    if (!dialog) return null;
    const titel =
      dialog.art === 'wiedervorlage'
        ? 'Wiedervorlage verschieben'
        : dialog.art === 'weitergeben'
          ? 'An Kollegen weitergeben'
          : dialog.art === 'stornieren'
            ? 'Stornieren'
            : dialog.art === 'oeffnen'
              ? 'Wieder öffnen'
              : dialog.schritt.label;
    return (
      <Dialog
        titel={titel}
        onClose={() => setDialog(null)}
        aktionen={
          <>
            <button onClick={() => setDialog(null)}>Abbrechen</button>
            <button className={dialog.art === 'stornieren' ? 'gefahr' : 'primary'} disabled={busy || !kannBestaetigen} onClick={bestaetigen}>
              Bestätigen
            </button>
          </>
        }
      >
        {fehler && <Notice kind="error">{fehler}</Notice>}
        {s?.hinweis && <Notice kind="info">{s.hinweis}</Notice>}

        {s?.felder?.includes('lieferant') && (
          <div className="grid2">
            <Field label="Lieferant (Pflicht)">
              <input value={lieferant} onChange={(e) => setLieferant(e.target.value)} autoFocus list="lieferanten" />
              <datalist id="lieferanten">
                {['ElectronicPartner', 'Expert', 'ASWO', 'Euronics', 'Amazon', 'Hersteller direkt'].map((l) => (
                  <option key={l} value={l} />
                ))}
              </datalist>
            </Field>
            <Field label="Bestellnummer beim Lieferanten">
              <input value={bestellnr} onChange={(e) => setBestellnr(e.target.value)} />
            </Field>
            <Field label="Erwartet am" hint="Wiedervorlage dann am Tag danach">
              <input type="date" value={erwartetAm} onChange={(e) => setErwartetAm(e.target.value)} />
            </Field>
          </div>
        )}
        {s?.felder?.includes('diagnose') && (
          <Field label="Diagnose">
            <textarea value={diagnose} onChange={(e) => setDiagnose(e.target.value)} style={{ minHeight: 60 }} />
          </Field>
        )}
        {s?.felder?.includes('kva') && (
          <Field label="Kostenvoranschlag (€, Pflicht)">
            <input value={kva} onChange={(e) => setKva(e.target.value)} inputMode="decimal" placeholder="z. B. 89,00" />
          </Field>
        )}

        {s?.abschluss && (
          <div className="field">
            <span>Wie wurde abgerechnet?</span>
            {ABRECHNUNG.filter((a) => abschlussArten.includes(a.wert)).map((a) => (
              <label key={a.wert} className="row" style={{ flexWrap: 'nowrap' }}>
                <input type="radio" style={{ width: 'auto' }} checked={abrechnung === a.wert} onChange={() => setAbrechnung(a.wert)} />
                {a.label}
              </label>
            ))}
            {abrechnung === 'kasse' && (
              <input placeholder={`${abrechnungBraucht} (Pflicht)`} value={beleg} onChange={(e) => setBeleg(e.target.value)} autoFocus />
            )}
            {abrechnung === 'forderung' && (
              <div className="grid2">
                <input placeholder="Offener Betrag € (Pflicht)" value={betrag} onChange={(e) => setBetrag(e.target.value)} inputMode="decimal" autoFocus />
                <input placeholder={abrechnungBraucht} value={beleg} onChange={(e) => setBeleg(e.target.value)} />
              </div>
            )}
          </div>
        )}

        {dialog.art === 'weitergeben' && (
          <Field label="An">
            <select value={an} onChange={(e) => setAn(e.target.value)}>
              <option value="">— bitte wählen —</option>
              {benutzer
                .filter((b) => b.id !== v.zustaendig_id)
                .map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
            </select>
          </Field>
        )}
        {dialog.art === 'oeffnen' && (
          <Field label="In Status">
            <select value={zielStatus} onChange={(e) => setZielStatus(e.target.value)}>
              {offeneStatus.map((o) => (
                <option key={o.wert} value={o.wert}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        {(dialog.art === 'wiedervorlage' || (s && !s.abschluss)) && (
          <>
            {dialog.art === 'schritt' && (
              <label className="row small">
                <input type="checkbox" style={{ width: 'auto' }} checked={eigeneFrist} onChange={(e) => setEigeneFrist(e.target.checked)} />
                Eigene Wiedervorlage statt der Vorgabe
              </label>
            )}
            {(dialog.art === 'wiedervorlage' || eigeneFrist) && (
              <div className="grid2">
                <Field label="Wiedervorlage am">
                  <input type="datetime-local" value={datum} onChange={(e) => setDatum(e.target.value)} />
                </Field>
                <Field label="Wartet auf (optional)">
                  <input value={wartenAuf} onChange={(e) => setWartenAuf(e.target.value)} placeholder="z. B. Rückmeldung Kunde" />
                </Field>
              </div>
            )}
          </>
        )}

        <Field
          label={
            dialog.art === 'weitergeben'
              ? 'Hinweis für den Kollegen (optional)'
              : dialog.art !== 'schritt'
                ? 'Begründung (Pflicht)'
                : s?.abschluss
                  ? abrechnung === 'keine'
                    ? 'Begründung (Pflicht)'
                    : 'Notiz (optional)'
                  : (s?.notizLabel ?? 'Notiz') + (s?.notizPflicht ? ' (Pflicht)' : ' (optional)')
          }
        >
          <textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} style={{ minHeight: 60 }} />
        </Field>
      </Dialog>
    );
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      {v.aufgabe && (
        <div>
          <div className="small muted">Als Nächstes</div>
          <strong>{v.aufgabe}</strong>
          {v.zustaendig_id !== user?.id && <span className="muted"> · zuständig: {v.zustaendig}</span>}
          {v.warten_auf && <div className="small muted">wartet auf: {v.warten_auf}</div>}
        </div>
      )}
      <div className="row">
        {schritte.map((sch) => (
          <button key={sch.label} className={sch.primary ? 'primary' : sch.abschluss ? 'ok' : ''} onClick={() => (sch.aktion ? sch.aktion() : oeffne({ art: 'schritt', schritt: sch }))}>
            {sch.label}
          </button>
        ))}
        {KONTAKT_STATUS[art].includes(v.status) && (
          <button disabled={busy} onClick={() => void los('nicht-erreicht', {})}>
            Kunde nicht erreicht
          </button>
        )}
      </div>
      <div className="row">
        <button className="klein" onClick={() => oeffne({ art: 'wiedervorlage' })}>
          Wiedervorlage verschieben
        </button>
        <button className="klein" onClick={() => oeffne({ art: 'weitergeben' })}>
          Weitergeben
        </button>
        {(art === 'forderung' ? can('admin') : can('admin', 'verkauf')) && (
          <button className="klein gefahr" onClick={() => oeffne({ art: 'stornieren' })}>
            Stornieren
          </button>
        )}
      </div>
      {fehler && !dialog && <Notice kind="error">{fehler}</Notice>}
      {renderDialog()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Positionen
// ---------------------------------------------------------------------------

export interface PositionEingabe {
  bezeichnung: string;
  artikelnummer: string;
  ean: string;
  menge: string;
  preis: string;
}

export const leerePosition = (): PositionEingabe => ({ bezeichnung: '', artikelnummer: '', ean: '', menge: '1', preis: '' });

export function positionenFuerApi(p: PositionEingabe[]) {
  return p
    .filter((x) => x.bezeichnung.trim())
    .map((x) => ({
      bezeichnung: x.bezeichnung.trim(),
      artikelnummer: x.artikelnummer.trim() || null,
      ean: x.ean.trim() || null,
      menge: Number(x.menge.replace(',', '.')) || 1,
      preisCent: centAusText(x.preis),
    }));
}

export const positionAusApi = (p: Position): PositionEingabe => ({
  bezeichnung: p.bezeichnung,
  artikelnummer: p.artikelnummer ?? '',
  ean: p.ean ?? '',
  menge: String(p.menge),
  preis: p.preis_cent == null ? '' : (p.preis_cent / 100).toFixed(2).replace('.', ','),
});

export function PositionenEditor({ positionen, onChange, mitPreis = true }: { positionen: PositionEingabe[]; onChange: (p: PositionEingabe[]) => void; mitPreis?: boolean }) {
  const setze = (i: number, feld: keyof PositionEingabe, wert: string) => onChange(positionen.map((p, j) => (j === i ? { ...p, [feld]: wert } : p)));
  const summe = positionenFuerApi(positionen).reduce((s, p) => s + (p.preisCent ?? 0) * p.menge, 0);
  return (
    <div className="stack" style={{ gap: 8 }}>
      {positionen.map((p, i) => (
        <div key={i} className="positionszeile">
          <input placeholder="Artikel / Bezeichnung" value={p.bezeichnung} onChange={(e) => setze(i, 'bezeichnung', e.target.value)} />
          <input placeholder="Art.-Nr." value={p.artikelnummer} onChange={(e) => setze(i, 'artikelnummer', e.target.value)} />
          <input placeholder="EAN" value={p.ean} onChange={(e) => setze(i, 'ean', e.target.value)} inputMode="numeric" />
          <input placeholder="Menge" value={p.menge} onChange={(e) => setze(i, 'menge', e.target.value)} inputMode="decimal" />
          {mitPreis && <input placeholder="VK € (brutto)" value={p.preis} onChange={(e) => setze(i, 'preis', e.target.value)} inputMode="decimal" />}
          <button type="button" className="klein" disabled={positionen.length === 1} onClick={() => onChange(positionen.filter((_, j) => j !== i))} aria-label="Position entfernen">
            ✕
          </button>
        </div>
      ))}
      <div className="row">
        <button type="button" className="link" onClick={() => onChange([...positionen, leerePosition()])}>
          + Position
        </button>
        <span className="spacer" />
        {mitPreis && summe > 0 && <strong>Summe {formatGeld(summe)}</strong>}
      </div>
    </div>
  );
}
